import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField, Typography, IconButton, ListItem, ListItemText, Stack } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { getProjects, getMaterialEquipment, getCompanies, createSiteDelivery, getSiteDeliveriesForProject, getSiteStockForProject, getMerchantDeliveryUploadUrl, getFinanceDocuments } from '../services/api';
import DateInputWithArrows from '../components/DateInputWithArrows';
import SiteStockList from '../components/SiteStockList';
import { formatDateEuropean } from '../utils/dateUtils';
import { UNITS } from '../constants/units';
import { useTranslation } from 'react-i18next';

const TYPE_OPTIONS = ['Material', 'Machine', 'Equipment'];

function emptyMerchantItem(units) {
  return {
    name: '',
    type: 'Material',
    unit: units || 'kom',
    quantity: '',
    unitPrice: '',
  };
}

async function uploadMerchantFile(projectId, file) {
  if (!file) return null;
  const presign = await getMerchantDeliveryUploadUrl({
    projectId: Number(projectId),
    originalName: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  await fetch(presign.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  return {
    objectKey: presign.data.objectKey,
    originalName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size || 0,
  };
}

function SiteDelivery() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [rows, setRows] = useState([]);
  const [siteStock, setSiteStock] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [reuseDocs, setReuseDocs] = useState({});
  const [form, setForm] = useState({
    sourceType: 'WAREHOUSE',
    materialEquipmentId: '',
    date: new Date().toISOString().slice(0, 10),
    quantity: '',
    notes: '',
    merchantNotes: '',
    financeDoc: 'DELIVERY_NOTE',
    partnerName: '',
    companyId: null,
    deliveryNoteNumber: '',
    invoiceNumber: '',
    deliveryNoteFile: null,
    invoiceFile: null,
    paidAtRegister: false,
    items: [emptyMerchantItem(UNITS.PIECE || 'kom')],
    existingNote: null,
  });
  const [noteOptions, setNoteOptions] = useState([]);

  const unitOptions = useMemo(() => Object.values(UNITS), []);
  const selectedWarehouseItem = useMemo(
    () => materials.find((m) => String(m.id) === String(form.materialEquipmentId)),
    [materials, form.materialEquipmentId]
  );
  const selectedWarehouseAvailable = useMemo(() => {
    if (!selectedWarehouseItem) return 0;
    if (selectedWarehouseItem.quantity == null) {
      return String(selectedWarehouseItem.type || '').toLowerCase() === 'machine' ? 1 : 0;
    }
    return Number(selectedWarehouseItem.quantity) || 0;
  }, [selectedWarehouseItem]);

  useEffect(() => {
    (async () => {
      const [p, m, c] = await Promise.all([getProjects(), getMaterialEquipment({ sort: 'name', order: 'asc' }), getCompanies()]);
      setProjects(p.data || []);
      setMaterials(m.data || []);
      setCompanies(c.data || []);
    })();
  }, []);

  const reloadProjectData = async (pid) => {
    const [delRes, stockRes, m] = await Promise.all([
      getSiteDeliveriesForProject(pid),
      getSiteStockForProject(pid),
      getMaterialEquipment({ sort: 'name', order: 'asc' }),
    ]);
    setRows(delRes.data || []);
    setSiteStock(stockRes.data || []);
    setMaterials(m.data || []);
  };

  useEffect(() => {
    (async () => {
      if (!projectId) {
        setRows([]);
        setSiteStock([]);
        return;
      }
      await reloadProjectData(projectId);
    })();
  }, [projectId]);

  const openModal = () => {
    setError('');
    setDuplicates([]);
    setReuseDocs({});
    setForm((prev) => ({
      ...prev,
      sourceType: 'WAREHOUSE',
      materialEquipmentId: '',
      date: new Date().toISOString().slice(0, 10),
      quantity: '',
      notes: '',
      merchantNotes: '',
      financeDoc: 'DELIVERY_NOTE',
      partnerName: '',
      companyId: null,
      deliveryNoteNumber: '',
      invoiceNumber: '',
      deliveryNoteFile: null,
      invoiceFile: null,
      paidAtRegister: false,
      items: [emptyMerchantItem(UNITS.PIECE || 'kom')],
      existingNote: null,
    }));
    setModalOpen(true);
  };

  const searchNotes = async (query) => {
    try {
      const res = await getFinanceDocuments({ type: 'DELIVERY_NOTE', search: query || undefined });
      setNoteOptions(res.data || []);
    } catch (err) {
      setNoteOptions([]);
    }
  };

  useEffect(() => {
    if (modalOpen && form.sourceType === 'MERCHANT') {
      searchNotes('').catch(() => {});
    }
  }, [modalOpen, form.sourceType]);

  const wantsDelivery = form.financeDoc === 'DELIVERY_NOTE' || form.financeDoc === 'BOTH';
  const wantsInvoice = form.financeDoc === 'INVOICE' || form.financeDoc === 'BOTH';
  const merchantItemsValid = form.items.every((item) => item.name && item.type && item.unit && Number(item.quantity) > 0);

  const submit = async (extra = {}) => {
    if (!projectId || !form.date) return;
    if (form.sourceType === 'WAREHOUSE') {
      if (!form.quantity || Number(form.quantity) <= 0) return;
      if (Number(form.quantity) > selectedWarehouseAvailable) {
        setError(`Only ${selectedWarehouseAvailable} available in warehouse for selected item.`);
        return;
      }
    }
    setSubmitting(true);
    let created = false;
    try {
      setError('');
      if (form.sourceType !== 'MERCHANT') {
        await createSiteDelivery({
          projectId: Number(projectId),
          sourceType: form.sourceType,
          materialEquipmentId: Number(form.materialEquipmentId),
          quantity: Number(form.quantity),
          deliveryDate: form.date,
          notes: form.notes || undefined,
        });
      } else {
        const deliveryNoteAttachment = wantsDelivery
          ? await uploadMerchantFile(projectId, form.deliveryNoteFile)
          : null;
        const invoiceAttachment = wantsInvoice
          ? await uploadMerchantFile(projectId, form.invoiceFile)
          : null;
        await createSiteDelivery({
          projectId: Number(projectId),
          sourceType: 'MERCHANT',
          deliveryDate: form.date,
          notes: form.notes || undefined,
          items: form.items.map((item) => ({
            name: item.name,
            type: item.type,
            unit: item.unit,
            quantity: Number(item.quantity),
            unitPrice: item.unitPrice === '' ? undefined : Number(item.unitPrice),
            notes: form.merchantNotes || undefined,
          })),
          financeDocuments: form.financeDoc === 'BOTH' ? ['DELIVERY_NOTE', 'INVOICE'] : [form.financeDoc],
          companyId: form.companyId || undefined,
          counterpartyName: form.partnerName || undefined,
          deliveryNoteNumber: form.deliveryNoteNumber || undefined,
          invoiceNumber: form.invoiceNumber || undefined,
          deliveryNoteAttachment: deliveryNoteAttachment || undefined,
          invoiceAttachment: invoiceAttachment || undefined,
          paidAtRegister: Boolean(form.paidAtRegister),
          confirmDuplicate: Boolean(extra.confirmDuplicate),
          existingDeliveryNoteId: extra.confirmDuplicate
            ? undefined
            : (extra.existingDeliveryNoteId || reuseDocs.existingDeliveryNoteId || form.existingNote?.id || undefined),
          existingInvoiceId: extra.confirmDuplicate ? undefined : (extra.existingInvoiceId || reuseDocs.existingInvoiceId || undefined),
        });
      }

      setModalOpen(false);
      setDuplicates([]);
      created = true;
    } catch (err) {
      if (err?.response?.data?.code === 'DUPLICATE') {
        setDuplicates(err.response.data.duplicates || []);
        setError(t('siteDelivery.duplicateWarning'));
      } else {
        setError(err?.response?.data?.error || t('siteDelivery.failedCreate'));
      }
    } finally {
      setSubmitting(false);
    }
    if (created) {
      await reloadProjectData(projectId);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('siteDelivery.title')}</Typography>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="proj">{t('siteDelivery.project')}</InputLabel>
                <Select labelId="proj" label={t('siteDelivery.project')} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {projectId && (
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button variant="contained" onClick={openModal}>{t('siteDelivery.addNew')}</Button>
            </Stack>
          )}
        </CardContent>
      </Card>
      {projectId && (
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('siteDelivery.deliveries')}</Typography>
                {rows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">{t('siteDelivery.empty')}</Typography>
                )}
                {rows.map((r) => {
                  const itemName = r.materialEquipment?.name || materials.find((me) => String(me.id) === String(r.description))?.name || `#${r.description}`;
                  return (
                    <ListItem key={r.id} divider disableGutters>
                      <ListItemText
                        primary={`${formatDateEuropean(r.deliveryDate)} – ${itemName} – qty: ${r.quantity}${r.financialDeliveryNoteId ? ` · DN #${r.financialDeliveryNoteId}` : ''}${r.financialInvoiceId ? ` · INV #${r.financialInvoiceId}` : ''}`}
                        secondary={`Source: ${r.sourceType || 'WAREHOUSE'}`}
                      />
                    </ListItem>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('siteStock.title')}</Typography>
                <SiteStockList items={siteStock} materials={materials} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth={form.sourceType === 'MERCHANT' ? 'md' : 'sm'}>
        <DialogTitle>{t('siteDelivery.addNewDelivery')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && (
              <Alert severity={duplicates.length ? 'warning' : 'error'}>{error}</Alert>
            )}
            {duplicates.map((dup) => (
              <Alert key={dup.id} severity="info">
                {t(`finance.types.${dup.type}`)} {dup.documentNumber} — {dup.counterpartyName}
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    const next = dup.type === 'INVOICE'
                      ? { ...reuseDocs, existingInvoiceId: dup.id }
                      : { ...reuseDocs, existingDeliveryNoteId: dup.id };
                    setReuseDocs(next);
                    submit(next);
                  }}
                >
                  {t('siteDelivery.useExisting')}
                </Button>
              </Alert>
            ))}
            {duplicates.length > 0 && (
              <Button size="small" onClick={() => submit({ confirmDuplicate: true })}>
                {t('siteDelivery.saveAnyway')}
              </Button>
            )}
            <FormControl fullWidth>
              <InputLabel id="delivery-source">{t('siteDelivery.source')}</InputLabel>
              <Select
                labelId="delivery-source"
                label={t('siteDelivery.source')}
                value={form.sourceType}
                onChange={(e) => setForm((prev) => ({ ...prev, sourceType: e.target.value }))}
              >
                <MenuItem value="WAREHOUSE">{t('siteDelivery.fromWarehouse')}</MenuItem>
                <MenuItem value="MERCHANT">{t('siteDelivery.fromMerchant')}</MenuItem>
              </Select>
            </FormControl>
            {form.sourceType === 'WAREHOUSE' ? (
              <>
                <FormControl fullWidth>
                  <InputLabel id="warehouse-item">{t('siteDelivery.material')}</InputLabel>
                  <Select
                    labelId="warehouse-item"
                    label={t('siteDelivery.material')}
                    value={form.materialEquipmentId}
                    onChange={(e) => setForm((prev) => ({ ...prev, materialEquipmentId: e.target.value }))}
                  >
                    {materials.map((me) => {
                      const available = me.quantity == null
                        ? (String(me.type || '').toLowerCase() === 'machine' ? 1 : 0)
                        : Number(me.quantity || 0);
                      return (
                        <MenuItem key={me.id} value={me.id} disabled={available <= 0}>
                          {`${me.name} (available: ${available})`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  type="number"
                  label={t('siteDelivery.amount')}
                  inputProps={{ step: 0.01, min: 0 }}
                  fullWidth
                  value={form.quantity}
                  onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
                />
                <DateInputWithArrows
                  fullWidth
                  label={t('siteDelivery.date')}
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                />
                <TextField
                  label={t('siteDelivery.deliveryNotes')}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="finance-doc">{t('siteDelivery.financeDoc')}</InputLabel>
                  <Select
                    labelId="finance-doc"
                    label={t('siteDelivery.financeDoc')}
                    value={form.financeDoc}
                    onChange={(e) => setForm((prev) => ({ ...prev, financeDoc: e.target.value }))}
                  >
                    <MenuItem value="DELIVERY_NOTE">{t('siteDelivery.financeDelivery')}</MenuItem>
                    <MenuItem value="INVOICE">{t('siteDelivery.financeInvoice')}</MenuItem>
                    <MenuItem value="BOTH">{t('siteDelivery.financeBoth')}</MenuItem>
                  </Select>
                </FormControl>
                <Alert severity="info">{t('siteDelivery.receivedOnlyHint')}</Alert>
                {wantsDelivery && (
                  <Autocomplete
                    options={noteOptions}
                    getOptionLabel={(opt) => `${opt.documentNumber || `#${opt.id}`} · ${opt.counterpartyName}`}
                    value={form.existingNote}
                    onChange={(_, value) => setForm((prev) => ({
                      ...prev,
                      existingNote: value,
                      partnerName: value?.counterpartyName || prev.partnerName,
                      companyId: value?.companyId || prev.companyId,
                      deliveryNoteNumber: value?.documentNumber || prev.deliveryNoteNumber,
                    }))}
                    onInputChange={(_, value) => { searchNotes(value).catch(() => {}); }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('siteDelivery.findExistingNote')}
                        helperText={t('siteDelivery.findExistingNoteHint')}
                      />
                    )}
                  />
                )}
                <Autocomplete
                  options={companies}
                  getOptionLabel={(opt) => opt.name || ''}
                  onChange={(_, value) => setForm((prev) => ({ ...prev, companyId: value?.id || null, partnerName: value?.name || prev.partnerName }))}
                  inputValue={form.partnerName}
                  onInputChange={(_, value) => setForm((prev) => ({ ...prev, partnerName: value }))}
                  freeSolo
                  renderInput={(params) => <TextField {...params} label={t('siteDelivery.partner')} />}
                />
                <DateInputWithArrows
                  fullWidth
                  label={t('siteDelivery.date')}
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                />
                {wantsDelivery && (
                  <>
                    <TextField
                      label={t('siteDelivery.deliveryNoteNumber')}
                      value={form.deliveryNoteNumber}
                      onChange={(e) => setForm((prev) => ({ ...prev, deliveryNoteNumber: e.target.value }))}
                      fullWidth
                    />
                    <Button component="label" variant="outlined">
                      {form.deliveryNoteFile ? form.deliveryNoteFile.name : t('siteDelivery.deliveryNoteAttachment')}
                      <input
                        hidden
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setForm((prev) => ({ ...prev, deliveryNoteFile: e.target.files?.[0] || null }))}
                      />
                    </Button>
                  </>
                )}
                {wantsInvoice && (
                  <>
                    <TextField
                      label={t('siteDelivery.invoiceNumber')}
                      value={form.invoiceNumber}
                      onChange={(e) => setForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                      fullWidth
                    />
                    <Button component="label" variant="outlined">
                      {form.invoiceFile ? form.invoiceFile.name : t('siteDelivery.invoiceAttachment')}
                      <input
                        hidden
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setForm((prev) => ({ ...prev, invoiceFile: e.target.files?.[0] || null }))}
                      />
                    </Button>
                    <FormControlLabel
                      control={<Checkbox checked={form.paidAtRegister} onChange={(e) => setForm((prev) => ({ ...prev, paidAtRegister: e.target.checked }))} />}
                      label={t('siteDelivery.paidAtRegister')}
                    />
                  </>
                )}
                <Typography variant="subtitle2">{t('siteDelivery.items')}</Typography>
                {form.items.map((item, idx) => (
                  <Grid container spacing={1} key={idx} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t('siteDelivery.materialName')}
                        value={item.name}
                        onChange={(e) => setForm((prev) => {
                          const next = [...prev.items];
                          next[idx] = { ...next[idx], name: e.target.value };
                          return { ...prev, items: next };
                        })}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>{t('siteDeparture.assignedItem')}</InputLabel>
                        <Select
                          label={t('siteDeparture.assignedItem')}
                          value={item.type}
                          onChange={(e) => setForm((prev) => {
                            const next = [...prev.items];
                            next[idx] = { ...next[idx], type: e.target.value };
                            return { ...prev, items: next };
                          })}
                        >
                          {TYPE_OPTIONS.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>{t('finance.unit')}</InputLabel>
                        <Select
                          label={t('finance.unit')}
                          value={item.unit}
                          onChange={(e) => setForm((prev) => {
                            const next = [...prev.items];
                            next[idx] = { ...next[idx], unit: e.target.value };
                            return { ...prev, items: next };
                          })}
                        >
                          {unitOptions.map((unit) => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <TextField
                        type="number"
                        label={t('siteDelivery.amount')}
                        inputProps={{ step: 0.01, min: 0 }}
                        fullWidth
                        value={item.quantity}
                        onChange={(e) => setForm((prev) => {
                          const next = [...prev.items];
                          next[idx] = { ...next[idx], quantity: e.target.value };
                          return { ...prev, items: next };
                        })}
                      />
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <TextField
                        type="number"
                        label={t('siteDelivery.unitPrice')}
                        inputProps={{ step: 0.01, min: 0 }}
                        fullWidth
                        value={item.unitPrice}
                        onChange={(e) => setForm((prev) => {
                          const next = [...prev.items];
                          next[idx] = { ...next[idx], unitPrice: e.target.value };
                          return { ...prev, items: next };
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      {form.items.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() => setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
                <Button onClick={() => setForm((prev) => ({ ...prev, items: [...prev.items, emptyMerchantItem(UNITS.PIECE || 'kom')] }))}>
                  {t('siteDelivery.addItem')}
                </Button>
                <TextField
                  label={t('siteDelivery.deliveryNotes')}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={
              submitting
              || !projectId
              || !form.date
              || (form.sourceType === 'WAREHOUSE' && (!form.quantity || Number(form.quantity) <= 0))
              || (form.sourceType === 'WAREHOUSE' && Number(form.quantity) > selectedWarehouseAvailable)
              || (form.sourceType === 'WAREHOUSE' && !form.materialEquipmentId)
              || (form.sourceType === 'MERCHANT' && (!form.partnerName || !merchantItemsValid || (wantsInvoice && !form.invoiceFile)))
            }
          >
            {t('siteDelivery.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SiteDelivery;


